import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, Mail, RotateCcw } from 'lucide-react';
import { SaveButton } from '../../components/ui/SaveButton';
import { reportError } from '../../lib/errors';
import toast from 'react-hot-toast';

type TemplateType = 'invoice' | 'quote' | 'report' | 'credit';

interface EmailTemplate {
    template_type: TemplateType;
    subject: string;
    body: string;
    is_default: boolean;
}

const TYPE_LABELS: Record<TemplateType, string> = {
    invoice: 'Invoice',
    quote: 'Quote',
    report: 'Service Report',
    credit: 'Credit Note',
};

const TYPE_ORDER: TemplateType[] = ['quote', 'invoice', 'report', 'credit'];

// Placeholders the send-time renderer substitutes. Shown as insertable chips.
const VARIABLES = [
    { token: '{{customer_name}}', label: 'Customer name' },
    { token: '{{business_name}}', label: 'Your business name' },
    { token: '{{doc_number}}', label: 'Document number' },
    { token: '{{amount}}', label: 'Amount' },
    { token: '{{vehicle}}', label: 'Vehicle' },
];

export const EmailTemplatesSection = () => {
    const [loading, setLoading] = useState(true);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [templates, setTemplates] = useState<Record<TemplateType, EmailTemplate>>({} as Record<TemplateType, EmailTemplate>);
    const [selected, setSelected] = useState<TemplateType>('quote');

    useEffect(() => {
        const load = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { setLoading(false); return; }
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'email-templates-section.profiles');
            if (!profile?.active_org_id) { setLoading(false); return; }
            setOrgId(profile.active_org_id);

            const { data, error } = await supabase.rpc('fn_get_email_templates', { p_org_id: profile.active_org_id });
            if (!error && Array.isArray(data)) {
                const byType = {} as Record<TemplateType, EmailTemplate>;
                for (const t of data as EmailTemplate[]) byType[t.template_type] = t;
                setTemplates(byType);
            }
            setLoading(false);
        };
        load();
    }, []);

    const current = templates[selected];

    const updateCurrent = (patch: Partial<EmailTemplate>) => {
        setTemplates(prev => ({
            ...prev,
            [selected]: { ...prev[selected], ...patch },
        }));
    };

    const insertVariable = (token: string) => {
        updateCurrent({ body: (current?.body || '') + token });
    };

    const handleSave = async () => {
        if (!orgId || !current) return;
        const { error } = await supabase.rpc('fn_upsert_email_template', {
            p_org_id: orgId,
            p_template_type: selected,
            p_subject: current.subject,
            p_body: current.body,
        });
        if (error) {
            toast.error('Failed to save email template');
            throw error;
        }
        updateCurrent({ is_default: false });
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-accent" />
                    Email Templates
                </h3>
                <p className="text-sm text-fg-2">
                    Word the emails your customers receive. Each document type has its own subject and message.
                    The rest of the email (your logo, business details, buttons) is added automatically.
                </p>
            </div>

            {/* Type selector */}
            <div className="flex flex-wrap gap-2">
                {TYPE_ORDER.map((t) => (
                    <button
                        key={t}
                        onClick={() => setSelected(t)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${selected === t
                            ? 'bg-accent/20 text-accent'
                            : 'bg-raised text-fg-2 hover:text-fg'
                            }`}
                    >
                        {TYPE_LABELS[t]}
                        {templates[t] && !templates[t].is_default && (
                            <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-accent align-middle" title="Customised" />
                        )}
                    </button>
                ))}
            </div>

            {current && (
                <div className="space-y-6">
                    {current.is_default && (
                        <div className="flex items-center gap-2 text-xs text-fg-2 bg-raised rounded-lg px-3 py-2">
                            <RotateCcw className="w-3.5 h-3.5" />
                            Using the default wording — edit and save to make it your own.
                        </div>
                    )}

                    {/* Subject */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-fg-2">Subject</label>
                        <input
                            type="text"
                            value={current.subject}
                            onChange={(e) => updateCurrent({ subject: e.target.value })}
                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                        />
                    </div>

                    {/* Body */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-fg-2">Message</label>
                        <textarea
                            value={current.body}
                            onChange={(e) => updateCurrent({ body: e.target.value })}
                            rows={9}
                            className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-3 text-fg text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all resize-y font-mono"
                        />
                    </div>

                    {/* Variable chips */}
                    <div className="space-y-2">
                        <p className="text-xs font-medium text-fg-2">
                            Insert a placeholder — it's filled in automatically when the email is sent:
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {VARIABLES.map((v) => (
                                <button
                                    key={v.token}
                                    onClick={() => insertVariable(v.token)}
                                    title={`Insert ${v.label}`}
                                    className="px-2 py-1 rounded-md bg-raised border border-line text-[11px] font-mono text-fg-2 hover:text-accent hover:border-accent/40 transition-colors"
                                >
                                    {v.token}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="pt-2">
                        <SaveButton onClick={handleSave} />
                    </div>
                </div>
            )}
        </div>
    );
};
